/**
 * Which practice items the hub shows, and how many sit behind each chip.
 *
 * Its own module, dependency-free from Relay's generated types, for the reason
 * `sessions.ts` and `debrief.ts` give: this is the testable seam, and a plain
 * `node --test` run has no loader for `.tsx`.
 */
import { t, type PlainKey } from '../i18n/t.ts';

/** A practice item, as `Learner.practiceItems` carries it. */
export type PractisableItem = {
  kind: string;
};

/** `null` is "all kinds", which is the hub's default and its usual state. */
export type KindFilter = string | null;

/**
 * The order kinds appear in, when the learner has them.
 *
 * Fixed rather than alphabetical or by count, so the chips do not reorder
 * under the learner's finger as a conversation changes the counts. Anything
 * not named here — a kind from a newer migration — sorts after these rather
 * than disappearing, which is the failure this list would otherwise cause.
 */
const KIND_ORDER = ['GRAMMAR', 'LEXIS', 'DISCOURSE', 'PROSODY'];

const KIND_LABELS: Partial<Record<string, PlainKey>> = {
  GRAMMAR: 'kind-grammar',
  LEXIS: 'kind-lexis',
  DISCOURSE: 'kind-discourse',
  PROSODY: 'kind-prosody',
};

/**
 * A kind's label, whichever case it arrives in. Every kind on the wire is now
 * the GraphQL `ErrorKind` (`GRAMMAR`); `PracticeItem.kind` was the enum's
 * lowercase string until 2026-09-24, and taking either case costs nothing. A
 * kind from a newer server falls back to its own name in title case.
 */
export function kindLabel(kind: string): string {
  const known = KIND_LABELS[kind.toUpperCase()];
  if (known) return t(known);
  return kind.charAt(0).toUpperCase() + kind.slice(1).toLowerCase();
}

/**
 * The name a learner reads for a pattern, rather than its wire slug.
 *
 * One entry per `ErrorPattern` in `tutor-core/src/domain.rs`; a slug from a
 * newer migration falls back to its words in sentence case, which is plainer
 * than a curated name but never an underscore.
 */
const PATTERN_LABELS: Partial<Record<string, PlainKey>> = {
  past_simple: 'pattern-past-simple',
  present_perfect: 'pattern-present-perfect',
  articles: 'pattern-articles',
  subject_verb_agreement: 'pattern-subject-verb-agreement',
  prepositions: 'pattern-prepositions',
  countable_uncountable: 'pattern-countable-uncountable',
  comparatives: 'pattern-comparatives',
  conditionals: 'pattern-conditionals',
  phrasal_verbs: 'pattern-phrasal-verbs',
  false_friends: 'pattern-false-friends',
  register: 'pattern-register',
  discourse_markers: 'pattern-discourse-markers',
  filler_density: 'pattern-filler-density',
  tense_consistency: 'pattern-tense-consistency',
  pluralization: 'pattern-pluralization',
  verb_form: 'pattern-verb-form',
  word_order: 'pattern-word-order',
  modal_verbs: 'pattern-modal-verbs',
  possessives: 'pattern-possessives',
  other: 'pattern-other',
};

export function patternLabel(pattern: string): string {
  const known = PATTERN_LABELS[pattern];
  if (known) return t(known);
  const words = pattern.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * What each pattern is about, in a line a learner or a parent can read. The
 * example sentence beside it is English being practised, so it comes from the
 * server (`PracticeItem.patternExample`, kid-practice §3.1) and never from the
 * catalog.
 *
 * `other` has no entry: it names no pattern, so there is nothing to explain.
 */
const PATTERN_ABOUT: Partial<Record<string, PlainKey>> = {
  past_simple: 'pattern-past-simple-about',
  present_perfect: 'pattern-present-perfect-about',
  articles: 'pattern-articles-about',
  subject_verb_agreement: 'pattern-subject-verb-agreement-about',
  prepositions: 'pattern-prepositions-about',
  countable_uncountable: 'pattern-countable-uncountable-about',
  comparatives: 'pattern-comparatives-about',
  conditionals: 'pattern-conditionals-about',
  phrasal_verbs: 'pattern-phrasal-verbs-about',
  false_friends: 'pattern-false-friends-about',
  register: 'pattern-register-about',
  discourse_markers: 'pattern-discourse-markers-about',
  filler_density: 'pattern-filler-density-about',
  tense_consistency: 'pattern-tense-consistency-about',
  pluralization: 'pattern-pluralization-about',
  verb_form: 'pattern-verb-form-about',
  word_order: 'pattern-word-order-about',
  modal_verbs: 'pattern-modal-verbs-about',
  possessives: 'pattern-possessives-about',
};

export function patternAbout(pattern: string): string | null {
  const about = PATTERN_ABOUT[pattern];
  return about ? t(about) : null;
}

export type Chip = { kind: KindFilter; label: string; count: number };

/**
 * The chips to render: "All", then one per kind the learner actually has.
 *
 * Derived from the items rather than from a fixed list of the four kinds, so a
 * learner working on grammar alone gets two chips instead of three empty ones.
 */
export function chips(items: readonly PractisableItem[]): Chip[] {
  const counts = new Map<string, number>();
  for (const i of items) counts.set(i.kind, (counts.get(i.kind) ?? 0) + 1);

  const ranked = [...counts.keys()].sort((a, b) => {
    const ia = KIND_ORDER.indexOf(a.toUpperCase());
    const ib = KIND_ORDER.indexOf(b.toUpperCase());
    // Unknown kinds (-1) sort last, and among themselves alphabetically, so
    // the order is total rather than dependent on Map insertion.
    if (ia !== ib) return (ia < 0 ? KIND_ORDER.length : ia) - (ib < 0 ? KIND_ORDER.length : ib);
    return a.localeCompare(b);
  });

  return [
    { kind: null, label: t('chip-all'), count: items.length },
    ...ranked.map((k) => ({ kind: k as KindFilter, label: kindLabel(k), count: counts.get(k)! })),
  ];
}

/**
 * The cards to render.
 *
 * The server already orders `practiceItems` most-recurrent first and excludes
 * retired ones, so this filters and never sorts: a second ranking here would be
 * a second answer to a question §6 has already answered.
 */
export function visibleItems<T extends PractisableItem>(items: readonly T[], kind: KindFilter): T[] {
  return kind === null ? [...items] : items.filter((i) => i.kind === kind);
}

/**
 * The exemplar a register may see.
 *
 * An exemplar is the learner's own mistake beside its correction, and §8.2
 * keeps a child's errors off their screen — the premise IPA spec §2 states and
 * the hub did not hold. A child keeps the pattern and the way into practising
 * it; only the correction goes.
 */
export function exemplarFor<E>(exemplar: E | null | undefined, register: WordRegister): E | null {
  return register === 'adult' ? (exemplar ?? null) : null;
}

/** `PracticeItem.patternExample`: English being practised, and an emoji cue. */
export type PatternExampleView = { text: string; picture: string | null | undefined };

/**
 * All a child's card may show of a practice item (kid-practice §6).
 *
 * A projection rather than conditions in JSX, because "a carefully-worded
 * render is one careless edit from leaking" (progress design §3). The type has
 * no field for occurrences, kind, dates, outcomes or the exemplar, so the kid
 * branch of a card cannot render one: for a pattern, each of those is a
 * record of the child's own mistake.
 */
export type KidCard = {
  id: string;
  pattern: string;
  practisable: boolean;
  patternExample: PatternExampleView | null;
};

export function kidCard(item: {
  id: string;
  pattern: string;
  practisable?: boolean | null;
  patternExample?: PatternExampleView | null;
}): KidCard {
  return {
    id: item.id,
    pattern: item.pattern,
    practisable: item.practisable ?? false,
    patternExample: item.patternExample ?? null,
  };
}

/** What the hub says when there is nothing to show. */
export function emptyMessage(kind: KindFilter): string {
  return kind === null ? t('practice-empty') : t('practice-empty-kind', { kind: kindLabel(kind) });
}

/**
 * The filter the URL is asking for.
 *
 * The hub's filter lives in the query string rather than in component state so
 * that a debrief's focus area — which links to `/practice?focus=LEXIS` — and the
 * chip row are one mechanism. The screen used to carry both: a chip row *and* a
 * separate "Showing LEXIS / View all" banner that only the deep link could
 * produce, which meant two controls for one idea and two ways to disagree about
 * what was on screen.
 *
 * An empty or whitespace `focus` reads as no filter rather than as a kind no
 * item has, so `?focus=` does not present an empty hub with no way out of it.
 */
export function filterFromParams(params: URLSearchParams): KindFilter {
  const focus = params.get('focus')?.trim();
  return focus ? focus : null;
}

/**
 * The query string for a filter change.
 *
 * Returns a new object rather than mutating: `useSearchParams` hands back the
 * live one, and writing to it in place changes what the current render is
 * reading from. Parameters other than `focus` are carried through — nothing
 * else uses one today, and silently dropping a stranger's is the kind of thing
 * that is discovered much later.
 */
export function paramsWithFilter(params: URLSearchParams, next: KindFilter): URLSearchParams {
  const out = new URLSearchParams(params);
  if (next === null) out.delete('focus');
  else out.set('focus', next);
  return out;
}

// ---------------------------------------------------------------------------
// Words — vocabulary PRD §5 and §8
// ---------------------------------------------------------------------------

/** The two kinds of thing the hub lists. */
export type Surface = 'patterns' | 'words';

/**
 * Which surface the URL is asking for.
 *
 * In the query string like the kind filter, for the same reason: a debrief can
 * link straight to the word list, and Back behaves. Anything but `words` is
 * patterns, the hub's default and what every existing link expects.
 */
export function surfaceFromParams(params: URLSearchParams): Surface {
  return params.get('surface') === 'words' ? 'words' : 'patterns';
}

/**
 * The query string for a surface change.
 *
 * Drops `focus`, because it belongs to the surface being left: a pattern kind
 * carried into the word list is a source no word has, and the list would open
 * empty with no chip selected to explain it. `patterns` is the default and is
 * written as the absence of the parameter, so the hub's own URL stays clean.
 */
export function paramsWithSurface(params: URLSearchParams, next: Surface): URLSearchParams {
  const out = new URLSearchParams(params);
  out.delete('focus');
  if (next === 'words') out.set('surface', 'words');
  else out.delete('surface');
  return out;
}

/** A word, as `Learner.vocabularyItems` carries it. */
export type ListedWord = { source: string };

/** The register a word list is shown in — see `profile/register.ts`. */
export type WordRegister = 'kid' | 'adult';

/**
 * Whether this register may see a word harvested this way.
 *
 * §8: a `CORRECTED` word is a record of a mistake the learner made, and §8.2
 * forbids showing a child their own errors — so corrected words are adult
 * only. Any source this does not recognise is treated like a correction for a
 * child: a value from a newer server is not evidence it is safe to show.
 */
export function visibleIn(source: string, register: WordRegister): boolean {
  return register === 'adult' || source === 'ASKED';
}

/**
 * The words this register may see, then the ones this chip selects.
 *
 * In that order, and not the other one. §8 gates on the source, and a kid who
 * selected the "corrected" chip would otherwise be shown exactly the words the
 * gate exists to hide. The gate is not a filter among filters; it decides what
 * the list contains before any filter runs.
 *
 * Filters and never sorts: the server orders the list most recently met first
 * (§6), and a second ordering here would be a second answer to that.
 */
export function visibleWords<T extends ListedWord>(
  words: readonly T[],
  source: KindFilter,
  register: WordRegister,
): T[] {
  const allowed = words.filter((w) => visibleIn(w.source, register));
  return source === null ? allowed : allowed.filter((w) => w.source === source);
}

/** How a source reads on a chip. */
const SOURCE_LABELS: Partial<Record<string, PlainKey>> = {
  ASKED: 'chip-asked',
  CORRECTED: 'chip-corrected',
};

function sourceLabel(source: string): string {
  const known = SOURCE_LABELS[source];
  return known ? t(known) : kindLabel(source);
}

const SOURCE_ORDER = ['ASKED', 'CORRECTED'];

/**
 * The word chips: "All", then one per source the learner has — `chips`' shape,
 * so the hub has one filtering mechanism rather than two (§5).
 *
 * Counted after the register gate, so a child is offered no "Corrected" chip at
 * all rather than one whose count is the number of mistakes being hidden from
 * them.
 */
export function wordChips(words: readonly ListedWord[], register: WordRegister): Chip[] {
  const shown = visibleWords(words, null, register);
  const counts = new Map<string, number>();
  for (const w of shown) counts.set(w.source, (counts.get(w.source) ?? 0) + 1);
  const ranked = [...counts.keys()].sort((a, b) => {
    const ia = SOURCE_ORDER.indexOf(a);
    const ib = SOURCE_ORDER.indexOf(b);
    if (ia !== ib) return (ia < 0 ? SOURCE_ORDER.length : ia) - (ib < 0 ? SOURCE_ORDER.length : ib);
    return a.localeCompare(b);
  });
  return [
    { kind: null, label: t('chip-all'), count: shown.length },
    ...ranked.map((s) => ({ kind: s as KindFilter, label: sourceLabel(s), count: counts.get(s)! })),
  ];
}

/** What the word list says when there is nothing to show. */
export function emptyWordsMessage(source: KindFilter): string {
  return source === null ? t('words-empty') : t('words-empty-filtered');
}
